import { getTenantHost, isAdminPortal } from "@/utilities/domain"

/**
 * A company address ({company}.{domain}) has no website or POS of its own:
 * each branch has its own at {branch}-{company}.{domain}. Once the company
 * has a branch, its company address forwards to the first branch, keeping the
 * path so old links and bookmarks still land on the right page.
 *
 * Resolves to true when the browser is being sent elsewhere.
 */
export async function redirectToBranchHost(): Promise<boolean> {
  if (isAdminPortal()) return false

  const host = getTenantHost()
  const cacheKey = `portal:${host}`
  try {
    if (sessionStorage.getItem(cacheKey) === "branch") return false
  } catch {
    // Storage can be unavailable (private mode); the check below still runs.
  }

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 5000)
  try {
    const response = await fetch(`${import.meta.env.VITE_BASE_URL}/portal`, {
      headers: { Accept: "application/json", "X-Tenant": host },
      signal: controller.signal,
    })
    if (!response.ok) return false

    const { portal, redirect_host: target } = (await response.json()).data ?? {}
    if (target && target !== host) {
      const { protocol, port, pathname, search, hash } = window.location
      window.location.replace(
        `${protocol}//${target}${port ? `:${port}` : ""}${pathname}${search}${hash}`
      )
      return true
    }

    if (portal === "branch") sessionStorage.setItem(cacheKey, "branch")
  } catch {
    // If the check fails the app still loads on the address that was asked for.
  } finally {
    clearTimeout(timeout)
  }

  return false
}
